/**
 * Weighted mean + dispersion aggregator for the per-axis scoring architecture.
 *
 * Each axis is scored independently by its own LLM call; this combines the
 * axis levels into a final level by a **weighted mean** over ONE distribution
 * of axes (less hostile to uneven IC profiles than `min`, which was hostage
 * to the single most conservative axis), and uses the **dispersion** between
 * the axis levels as a reliability signal: a tight cluster is a confident
 * read; a wide spread (e.g. L2 on one axis, L5 on another) is an uneven
 * profile we should flag as lower-confidence rather than collapse to either
 * extreme.
 *
 * Weights are importance weights set on the model's axes (default 1.0 — a
 * flat-weight model reproduces the plain mean). They are normalized over the
 * SCORED axes only, so an abstention renormalizes the remaining mass instead
 * of dragging the mean. Per-axis LLM confidence never enters the weights or
 * the mean — it would reintroduce run-to-run variance; confidence propagates
 * only into the explainability layer as reliability tags.
 *
 * Parameterised over an explicit axis list + level range. Callers pass either
 * one **section**'s axes (cross or one discipline's verticals — sections are
 * informational, computed with the same formulas restricted to their axes) or
 * ALL run axes for the overall (see `combineOverall`). The level clamp comes
 * from the model's `levels`.
 *
 * Pure — no I/O.
 */

import type { WhyPoint } from "./level-assessment";

export interface AxisLevelScore {
  /** Axis id (string, validated by the caller against `model.axes[].id`). */
  axis: string;
  /** Integer level in `[model.levels.min, model.levels.max]`, or null when
   *  the axis abstained (insufficient signal). */
  level: number | null;
  /** Optional per-axis reasoning from the axis call, passed through. */
  reasoning?: string;
  /** Optional per-axis confidence (0–1). Not used by the aggregator (which
   *  derives reliability from dispersion + coverage), but surfaced to the UI
   *  so the operator can see how confidently the model pinned each axis.
   *  Since the 2026-07 contract change this is DERIVED deterministically from
   *  `confidenceBand` (band midpoint) — the LLM never emits a raw float. */
  confidence?: number;
  /** Evidence-state band the scorer picked ("convergent" | "corroborated" |
   *  "conjunction_only" | "baseline"). Source of `confidence`. Additive. */
  confidenceBand?: string;
  /** Set when the boundary-agreement pass sampled extra reads and they
   *  disagreed on the level — the axis kept its first read with a downgraded
   *  band and is flagged for human review. Additive. */
  lowAgreement?: boolean;
  // ── Contestability fields (passed through; not used by the aggregator) ──
  /** Concise statement of the rubric rule the level anchored on. */
  ruleApplied?: string;
  /** Why the inferred level holds. */
  whyThisLevel?: WhyPoint[];
  /** Why the candidate does NOT reach one level higher. */
  whyNotHigher?: WhyPoint[];
  /** Why the candidate clears the level just below. */
  whyNotLower?: WhyPoint[];
  /** On abstention: what evidence was missing. */
  missingEvidence?: string[];
}

export interface MeanAggregationResult {
  /** Rounded weighted mean of the scored axes, clamped to the model's level
   *  range. Null only when every axis abstained. */
  finalLevel: number | null;
  /** Raw (unrounded) weighted mean of scored axis levels. Null when all
   *  abstained. */
  mean: number | null;
  /** Weighted population standard deviation of scored axis levels (the
   *  dispersion). Null when fewer than 2 axes scored. */
  stddev: number | null;
  /** 0–1 reliability. High when axes agree (low dispersion) AND the scored
   *  axes carry all of the intended weight mass. Falls with spread and with
   *  the weight mass lost to abstentions. */
  reliability: number;
  /** Ids of axes that returned null (insufficient signal). */
  abstainedAxes: string[];
  /** The per-axis inputs, in the model's canonical axis order. */
  perAxis: AxisLevelScore[];
  /** Short factual note. The long PT-BR prose this used to carry was rendered
   *  nowhere — retired; persisted schema fields stay optional-with-default. */
  rationale: string;
}

/** Spread (in levels) at which dispersion-reliability hits 0. A std-dev of 2
 *  across an L0–L7 scale already means the axes disagree by ~2 levels on
 *  average — effectively no consensus. */
const MAX_STDDEV = 2;

function clamp(value: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, value));
}

/** A section's (or run's) axes for aggregation — id (canonical order), an
 *  optional label, and the axis's importance weight (default 1 = flat). */
export interface AggregationAxis {
  id: string;
  label?: string;
  /** Importance weight (> 0, finite). Missing → 1 (flat). */
  weight?: number;
}

/** Weighted moments of one distribution: weights are normalized over the
 *  given entries (the scored axes), so `Σ w′ᵢ = 1`. */
function weightedMoments(entries: Array<{ level: number; weight: number }>): {
  mean: number;
  stddev: number;
} {
  const totalWeight = entries.reduce((acc, e) => acc + e.weight, 0);
  const mean = entries.reduce((acc, e) => acc + (e.weight / totalWeight) * e.level, 0);
  const variance = entries.reduce(
    (acc, e) => acc + (e.weight / totalWeight) * (e.level - mean) ** 2,
    0,
  );
  return { mean, stddev: Math.sqrt(variance) };
}

/**
 * Aggregate per-axis levels into a final level via the weighted mean, with
 * dispersion-based reliability. Expects one score per axis in `axes`, but is
 * lenient about ordering and about missing entries (treated as abstentions).
 * An empty `axes` list yields a null-level result (used for the vertical
 * section on a cross-only run).
 *
 * Statistics are moments of ONE weighted distribution: weights normalize over
 * the SCORED axes only (`w′ᵢ = wᵢ / Σⱼ wⱼ`, j over scored axes — abstention
 * renormalization), `μ = Σ w′ᵢ·xᵢ`, `σ² = Σ w′ᵢ·(xᵢ − μ)²` (weighted
 * population variance). Coverage is the **weight mass** scored over the
 * weight mass of all given axes; reliability = `clamp(1 − σ/2, 0, 1) ×
 * coverage`.
 */
export function aggregateByMeanDispersion(opts: {
  axes: AggregationAxis[];
  levels: { min: number; max: number };
  scores: AxisLevelScore[];
}): MeanAggregationResult {
  const { axes, levels, scores } = opts;
  const { min: minLevel, max: maxLevel } = levels;
  // Canonicalize to the given axes in fixed order; missing → abstention.
  const byAxis = new Map(scores.map((s) => [s.axis, s]));
  const perAxis: AxisLevelScore[] = axes.map(
    (axis) => byAxis.get(axis.id) ?? { axis: axis.id, level: null },
  );

  const abstainedAxes = perAxis.filter((s) => s.level === null).map((s) => s.axis);
  const weightById = new Map(axes.map((a) => [a.id, a.weight ?? 1]));
  const scored = perAxis
    .filter((s): s is AxisLevelScore & { level: number } => s.level !== null)
    .map((s) => ({
      level: clamp(s.level, minLevel, maxLevel),
      weight: weightById.get(s.axis) ?? 1,
    }));

  if (scored.length === 0) {
    return {
      finalLevel: null,
      mean: null,
      stddev: null,
      reliability: 0,
      abstainedAxes,
      perAxis,
      rationale: "",
    };
  }

  const { mean, stddev: rawStddev } = weightedMoments(scored);
  const finalLevel = clamp(Math.round(mean), minLevel, maxLevel);
  const stddev = scored.length >= 2 ? rawStddev : null;

  // Reliability blends agreement (low dispersion) and coverage (how much of
  // the intended weight mass actually scored). Both in [0,1]; product so
  // either one being poor pulls confidence down.
  const dispersionReliability = stddev === null ? 1 : clamp(1 - stddev / MAX_STDDEV, 0, 1);
  const totalWeightMass = axes.reduce((acc, a) => acc + (a.weight ?? 1), 0);
  const scoredWeightMass = scored.reduce((acc, e) => acc + e.weight, 0);
  const coverage = totalWeightMass > 0 ? scoredWeightMass / totalWeightMass : 0;
  const reliability = clamp(dispersionReliability * coverage, 0, 1);

  return {
    finalLevel,
    mean,
    stddev,
    reliability,
    abstainedAxes,
    perAxis,
    rationale: `μ ${mean.toFixed(2)} · ${scored.length} eixo(s)`,
  };
}

/**
 * Weighted overall across ALL run axes (cross ∪ vertical) — a single weighted
 * mean over one distribution, replacing the old mean-of-section-means. A
 * section's axis count no longer biases the overall through section framing:
 * every axis enters with its own weight (flat 1.0 weights → every axis carries
 * equal mass, regardless of which section it belongs to).
 *
 * Edge cases keep the previous semantics: zero scored axes → null overall;
 * one scored axis → stddev null.
 */
export function combineOverall(opts: {
  /** ALL of the run's axes (cross + vertical), with their weights. */
  axes: AggregationAxis[];
  levels: { min: number; max: number };
  /** All per-axis scores of the run (any order; missing axes = abstention). */
  scores: AxisLevelScore[];
}): {
  overallLevel: number | null;
  overallMean: number | null;
  /** Weighted population std-dev across all scored run axes. Null with <2. */
  overallStddev: number | null;
  overallConfidence: number;
  overallRationale: string;
} {
  const agg = aggregateByMeanDispersion(opts);
  return {
    overallLevel: agg.finalLevel,
    overallMean: agg.mean,
    overallStddev: agg.stddev,
    overallConfidence: agg.reliability,
    overallRationale: agg.rationale,
  };
}
